/**
 * Enhanced Route Tracker Page V3
 * Streamlined interface with calendar indicators and improved UX
 *
 * Features:
 * - Calendar with pending status indicators
 * - Single-page workflow with expandable sections
 * - Progress tracking for each route
 * - Bulk actions for common statuses
 * - Mobile-first responsive design
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WorkSummaryDrawer } from '../components/WorkSummaryDrawer';
import { useVisibleServiceEventsMap } from '../hooks/useServiceEventCompletion';
import { SectionHeader } from '../components/route-tracker/SectionHeader';
import { StatusUpdatesSection } from '../components/route-tracker/StatusUpdatesSection';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SecurityBoundary } from '@/lib/security';
import { PermissionScope } from '@/types';
import { logDataAccess } from '@/lib/security/audit';
import { logUserActivity } from '@/lib/activity/userActivity';
import { useCurrentTenantId } from '@/contexts/TenantContext';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { removeCache } from '@/lib/utils/cache';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  CheckCircle2,
  AlertCircle,
  MapPin,
  Home,
  ChevronDown,
  ChevronRight,
  Save,
  Car,
  Clock,
  FileText,
  RefreshCw,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  Circle,
  DollarSign,
  MapPinOff,
  Wrench,
  Info,
  CalendarClock
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { RELEASE_REASON_LABELS, isRecyclingReason } from '@/lib/lifecycle/releaseDisposition';
import { CopyableVin } from '@/components/CopyableVin';
import { routeTrackingAPI, RecallStatusUpdate } from '@/lib/supabase/routeTracking';
import { updateRouteStatus } from '@/lib/supabase/technicianRoutes';
import useTechnicianAssignments, { assignmentKeys } from '@/hooks/useTechnicianAssignments';
import { routeKeys } from '@/features/routing/hooks/useTechnicianRoutes';
import { dailyRouteSummaryKeys } from '@/features/routing/hooks/useDailyRouteSummary';
import { RouteTrackerItem, RouteRecallDetail } from '@/types/routeTracking';
import { useRoutingPermissions } from '../hooks/useRoutingPermissions';
import { getManualStopReasonLabel } from '@/lib/validation/manualStopValidation';
import { CustomerContactReveal } from '@/components/CustomerContactReveal';
import { CUSTOMER_STOP_LABEL, getStopLocationKey } from '@/lib/manualStops/locationKey';

/** Tracker grouping key: household for customer stops, dealer name otherwise. */
const trackerLocationKey = (recall: RouteRecallDetail): string =>
  getStopLocationKey({
    stop_kind: recall.stopKind,
    contact_id: recall.contactId,
    dealer_name: recall.dealerName || 'Unknown Dealer',
    dealer_address: recall.dealerAddress,
    address_line2: recall.addressLine2,
    dealer_postal_code: recall.dealerPostalCode
  });

/** Dispatcher note(s) and, for customer stops, the dispatcher-only contact reveal. */
const StopNotesAndContact: React.FC<{
  isCustomer: boolean;
  contactId: string | null | undefined;
  dispatcherNotes: string[];
}> = ({ isCustomer, contactId, dispatcherNotes }) => (
  <div className='pl-6 space-y-0.5'>
    {dispatcherNotes.map(note => (
      <p key={note} className='text-xs text-muted-foreground whitespace-pre-line'>
        <span className='font-medium text-foreground'>Note for technician: </span>
        {note}
      </p>
    ))}
    {isCustomer && <CustomerContactReveal contactId={contactId} context='route_details' />}
  </div>
);
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday } from 'date-fns';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';

// Enhanced recall detail with status tracking
interface EnhancedRecallDetail extends RouteRecallDetail {
  finalStatus?: 'completed' | 'vehicle_sold' | 'not_available' | 'incomplete' | 'other';
  otherReason?: string;
  statusNotes?: string;
  servicedByTechnicianId?: string;
  servicedByTechnicianName?: string;
  isPreferred?: boolean;
  statusUpdatedAt?: string;
}

// Calendar day status
interface DayStatus {
  date: Date;
  hasRoutes: boolean;
  hasPendingRecalls: boolean;
  totalRecalls: number;
  completedRecalls: number;
}

// Status options for recalls with improved icons and colors
const RECALL_STATUSES = [
  {
    value: 'completed',
    label: 'Completed',
    icon: CheckCircle2,
    color: 'text-green-600 dark:text-green-400'
  },
  {
    value: 'vehicle_sold',
    label: 'Vehicle Sold',
    icon: DollarSign,
    color: 'text-red-600 dark:text-red-400'
  },
  {
    value: 'not_available',
    label: 'Vehicle Not Available',
    icon: MapPinOff,
    color: 'text-orange-600 dark:text-orange-400'
  },
  {
    value: 'incomplete',
    label: 'Incomplete',
    icon: Clock,
    color: 'text-blue-600 dark:text-blue-400'
  },
  {
    value: 'other',
    label: 'Other Reason',
    icon: FileText,
    color: 'text-gray-600 dark:text-gray-400'
  }
] as const;

// Status options for manual stops (simplified: only complete/incomplete)
const MANUAL_STOP_STATUSES = [
  {
    value: 'completed',
    label: 'Complete',
    icon: CheckCircle2,
    color: 'text-green-600 dark:text-green-400'
  },
  {
    value: 'incomplete',
    label: 'Incomplete',
    icon: Clock,
    color: 'text-blue-600 dark:text-blue-400'
  }
] as const;

// Reasons for "other" status, derived from the single shared table rather than a third
// hand-maintained copy. Three of these return the recall to the pool and three end it
// permanently, which was previously indistinguishable on screen — so the picker now says
// which is which. Rewording a label here can no longer silently flip an outcome, because
// the disposition travels with the code, not the text.
const COMMON_REASONS = RELEASE_REASON_LABELS;

/* eslint-disable max-lines-per-function, complexity */
const RouteTrackerPageV3: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const tenantId = useCurrentTenantId();
  const { userProfile } = useAuth();
  const { activeTechnicians = [] } = useTechnicianAssignments();
  // Map activeTechnicians to match expected format
  const technicians = activeTechnicians.map(tech => ({
    id: tech.id,
    name: tech.full_name
  }));

  // Permission checks for technician read-only access
  const {
    canUpdateAnyRecallStatus,
    canUpdateOwnRecallStatus,
    canEditRoute,
    linkedTechnicianId,
    isLoading: permissionsLoading
  } = useRoutingPermissions();

  // State
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [routes, setRoutes] = useState<RouteTrackerItem[]>([]);
  const [monthlyRoutes, setMonthlyRoutes] = useState<Map<string, RouteTrackerItem[]>>(new Map());
  const [enhancedRecalls, setEnhancedRecalls] = useState<Map<string, EnhancedRecallDetail>>(
    new Map()
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedRoutes, setExpandedRoutes] = useState<Set<string>>(new Set());
  const [selectedRecalls, setSelectedRecalls] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<string>('');
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  // Track individually updated recall IDs
  const [individualUpdates, setIndividualUpdates] = useState<Set<string>>(new Set());
  // (Removed: expandedTechnicians state — it was used by the legacy status-group
  // collapsibles inside the prior "Recently Updated" block. TechDayCard manages its
  // own per-status collapse state internally now.)
  // Work summary drawer: opens when an advisor clicks a completed recall row
  const [workSummaryRecall, setWorkSummaryRecall] = useState<RouteRecallDetail | null>(null);
  const [workSummaryOpen, setWorkSummaryOpen] = useState(false);

  // Batch-fetch completion details for visible completed recalls. This drives two
  // things: (1) we only make a row clickable if there's actually data to show — preserves
  // pre-feature behavior for legacy completions that never captured details on the app;
  // (2) it primes the per-assignment React Query cache so the drawer renders instantly.
  const visibleCompletedAssignmentIds = useMemo(
    () =>
      routes.flatMap(route =>
        route.recalls
          .filter(r => r.status === 'completed' && r.assignmentId)
          .map(r => r.assignmentId)
      ),
    [routes]
  );
  const { data: serviceEventsMap } = useVisibleServiceEventsMap(visibleCompletedAssignmentIds);

  // Log page view on mount
  useEffect(() => {
    logUserActivity({
      category: 'route_tracking',
      action: 'view_route_tracker'
    });
  }, []);

  // Load routes for the entire month
  const loadMonthlyRoutes = useCallback(async () => {
    try {
      setLoading(true);
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);

      const routesData = await routeTrackingAPI.getRoutesFromTable({
        dateRange: {
          start: format(start, 'yyyy-MM-dd'),
          end: format(end, 'yyyy-MM-dd'),
          preset: 'custom'
        }
      });

      // Group routes by date
      const grouped = new Map<string, RouteTrackerItem[]>();
      routesData.forEach(route => {
        const dateKey = route.routeDate;
        if (!grouped.has(dateKey)) {
          grouped.set(dateKey, []);
        }
        const group = grouped.get(dateKey);
        if (group) {
          group.push(route);
        }
      });

      // Auto-transition draft/active routes to in_progress on route day
      const today = format(new Date(), 'yyyy-MM-dd');
      const allRoutes = Array.from(grouped.values()).flat();
      let transitionsOccurred = false;

      for (const route of allRoutes) {
        // Auto-transition routes that are not already in_progress or completed
        if (
          route.routeDate === today &&
          route.status !== 'in_progress' &&
          route.status !== 'completed'
        ) {
          try {
            const { success } = await updateRouteStatus(route.routeId, 'in_progress');
            if (success) {
              // Update the local data to reflect the transition
              route.status = 'in_progress';
              transitionsOccurred = true;
              // eslint-disable-next-line no-console
              console.log(`[RouteTracker] Auto-transitioned route ${route.routeId} to in_progress`);
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Error auto-transitioning route to in_progress:', err);
          }
        }
      }

      // Update grouped map with transitioned routes
      if (transitionsOccurred) {
        grouped.clear();
        allRoutes.forEach(route => {
          const dateKey = route.routeDate;
          if (!grouped.has(dateKey)) {
            grouped.set(dateKey, []);
          }
          grouped.get(dateKey)?.push(route);
        });
      }

      setMonthlyRoutes(grouped);

      // Load routes for selected date
      const selectedDateKey = format(selectedDate, 'yyyy-MM-dd');
      const selectedRoutes = grouped.get(selectedDateKey) || [];
      setRoutes(selectedRoutes);

      // Initialize enhanced recall details
      const enhancedMap = new Map<string, EnhancedRecallDetail>();
      selectedRoutes.forEach(route => {
        route.recalls.forEach(recall => {
          // Determine finalStatus from recall status and notes
          let finalStatus:
            | 'completed'
            | 'vehicle_sold'
            | 'not_available'
            | 'incomplete'
            | 'other'
            | undefined = undefined;

          if (recall.status === 'completed') {
            finalStatus = 'completed';
          } else if (recall.status === 'cancelled' || recall.status === 'in_progress') {
            // Use releaseStatus from database — map legacy 'not_there' to 'not_available'
            if (recall.releaseStatus) {
              finalStatus =
                recall.releaseStatus === 'not_there' ? 'not_available' : recall.releaseStatus;
            } else if (recall.status === 'in_progress') {
              finalStatus = 'incomplete';
            }
          }

          enhancedMap.set(recall.assignmentId, {
            ...recall,
            finalStatus,
            otherReason: recall.releaseReason,
            servicedByTechnicianId: route.technicianId,
            servicedByTechnicianName: route.technicianName
          });
        });
      });
      setEnhancedRecalls(enhancedMap);

      // Keep all routes collapsed by default (no auto-expansion)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load routes:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load route data.'
      });
    } finally {
      setLoading(false);
    }
  }, [currentMonth, selectedDate, toast]);

  useEffect(() => {
    loadMonthlyRoutes();
  }, [loadMonthlyRoutes]);

  // Get calendar days with status
  const calendarDays = useMemo((): DayStatus[] => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });

    return days.map(date => {
      const dateKey = format(date, 'yyyy-MM-dd');
      const dayRoutes = monthlyRoutes.get(dateKey) || [];

      let totalRecalls = 0;
      let completedRecalls = 0;
      let hasPending = false;

      dayRoutes.forEach(route => {
        route.recalls.forEach(recall => {
          totalRecalls++;
          // Check route data status (for saved updates from database)
          const hasCompletedStatus =
            recall.status === 'completed' ||
            recall.status === 'cancelled' ||
            recall.status === 'in_progress';
          // Check enhancedRecalls for unsaved changes or in-memory updates
          const enhanced = enhancedRecalls.get(recall.assignmentId);
          // A recall is considered completed if it has a status in route data or enhancedRecalls
          const hasFinalStatus = hasCompletedStatus || !!enhanced?.finalStatus;

          if (hasFinalStatus) {
            completedRecalls++;
          } else {
            hasPending = true;
          }
        });
      });

      return {
        date,
        hasRoutes: dayRoutes.length > 0,
        hasPendingRecalls: hasPending,
        totalRecalls,
        completedRecalls
      };
    });
  }, [currentMonth, monthlyRoutes, enhancedRecalls]);

  // Helper function to check if a route date is in the future
  const isRouteDateInFuture = useCallback((routeDate: string): boolean => {
    if (!routeDate) return false;

    // Parse route date (format: YYYY-MM-DD)
    const routeDateObj = new Date(routeDate);
    const today = new Date();

    // Set both dates to midnight for accurate day comparison
    today.setHours(0, 0, 0, 0);
    routeDateObj.setHours(0, 0, 0, 0);

    // Check if route date is after today
    return routeDateObj > today;
  }, []);

  // Update recall status - track individual updates (not bulk)
  const updateRecallStatus = useCallback(
    (assignmentId: string, updates: Partial<EnhancedRecallDetail>, isBulkUpdate = false) => {
      setEnhancedRecalls(prev => {
        const newMap = new Map(prev);
        const existing = newMap.get(assignmentId);
        if (existing) {
          newMap.set(assignmentId, {
            ...existing,
            ...updates,
            statusUpdatedAt: new Date().toISOString()
          });
        }
        return newMap;
      });
      // Track individual updates (not bulk)
      if (!isBulkUpdate && updates.finalStatus) {
        setIndividualUpdates(prev => new Set([...prev, assignmentId]));
      }
      setUnsavedChanges(true);
    },
    []
  );

  // Helper to check if a recall is a manual stop
  const isManualStop = useCallback((recall: RouteRecallDetail): boolean => {
    return recall.stopType === 'manual';
  }, []);

  // Get appropriate status options based on stop type
  const getStatusOptions = useCallback(
    (recall: RouteRecallDetail) => {
      return isManualStop(recall) ? MANUAL_STOP_STATUSES : RECALL_STATUSES;
    },
    [isManualStop]
  );

  // Bulk update selected recalls
  const applyBulkStatus = useCallback(() => {
    if (!bulkStatus || selectedRecalls.size === 0) return;

    // Check if any selected recalls are scheduled for future dates
    const futureDatedRecalls: string[] = [];
    selectedRecalls.forEach(assignmentId => {
      // Find the route that contains this recall
      const routeForRecall = routes.find(route =>
        route.recalls.some(recall => recall.assignmentId === assignmentId)
      );

      // Check if route date is in the future
      if (routeForRecall && isRouteDateInFuture(routeForRecall.routeDate)) {
        futureDatedRecalls.push(assignmentId);
      }
    });

    // If any recalls are scheduled for the future, show error and prevent update
    if (futureDatedRecalls.length > 0) {
      const recallCount = futureDatedRecalls.length;
      const recallText = recallCount === 1 ? 'recall' : 'recalls';
      toast({
        title: 'Cannot Update Status',
        description:
          `You cannot update the status of ${recallCount} ${recallText} ` +
          'scheduled for future dates.',
        variant: 'destructive'
      });
      return;
    }

    // All selected recalls are valid, proceed with bulk update
    const updateCount = selectedRecalls.size;
    selectedRecalls.forEach(assignmentId => {
      updateRecallStatus(
        assignmentId,
        {
          finalStatus: bulkStatus as EnhancedRecallDetail['finalStatus']
        },
        true // Mark as bulk update
      );
    });

    setSelectedRecalls(new Set());
    setBulkStatus('');

    // Log bulk status activity
    logUserActivity({
      category: 'route_tracking',
      action: 'bulk_status_applied',
      itemCount: updateCount,
      metadata: { new_status: bulkStatus, recall_count: updateCount }
    });

    toast({
      title: 'Bulk Update Applied',
      description: `Updated ${updateCount} recall${updateCount === 1 ? '' : 's'} to ${bulkStatus}.`
    });
  }, [bulkStatus, selectedRecalls, updateRecallStatus, toast, routes, isRouteDateInFuture]);

  // Toggle recall selection
  const toggleRecallSelection = useCallback((assignmentId: string) => {
    setSelectedRecalls(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assignmentId)) {
        newSet.delete(assignmentId);
      } else {
        newSet.add(assignmentId);
      }
      return newSet;
    });
  }, []);

  // Select all recalls for the selected date (only editable ones)
  const allRecallIds = useMemo(
    () => new Set(routes.flatMap(r => r.recalls.map(rec => rec.assignmentId))),
    [routes]
  );

  // Get only editable recall IDs (based on permissions)
  const editableRecallIds = useMemo(
    () =>
      new Set(
        routes.filter(r => canEditRoute(r)).flatMap(r => r.recalls.map(rec => rec.assignmentId))
      ),
    [routes, canEditRoute]
  );

  // Check if user has any editable routes
  const hasEditableRoutes = useMemo(
    () => routes.some(r => canEditRoute(r)),
    [routes, canEditRoute]
  );

  const handleSelectAll = useCallback(() => {
    // Only select recalls the user can edit
    setSelectedRecalls(new Set(editableRecallIds));
    logUserActivity({
      category: 'route_tracking',
      action: 'select_all_recalls',
      itemCount: editableRecallIds.size,
      metadata: { selected_date: format(selectedDate, 'yyyy-MM-dd') }
    });
  }, [editableRecallIds, selectedDate]);

  // Auto-transition routes: in_progress → completed when all recalls have status in DATABASE
  // This checks the actual recall.status field (from database), not local enhanced state
  const autoTransitionToCompleted = useCallback(
    async (route: RouteTrackerItem) => {
      // Only check routes that are in_progress
      if (route.status !== 'in_progress') return;

      // Check if all recalls have a saved status in the DATABASE
      // recall.status comes from the database, not local state
      const allRecallsHaveSavedStatus = route.recalls.every(recall => {
        // These statuses indicate the recall has been processed in the database
        return (
          recall.status === 'completed' ||
          recall.status === 'cancelled' ||
          recall.status === 'in_progress'
        );
      });

      // A route is finished when every recall on it was processed. An EMPTY
      // route is not finished work: removing the last recall used to mark the
      // route `completed`, which is the likely origin of the 358 completed
      // routes in production that have no recalls at all.
      const allRecallsProcessed = route.recalls.length > 0 && allRecallsHaveSavedStatus;

      if (allRecallsProcessed) {
        try {
          const { success } = await updateRouteStatus(route.routeId, 'completed');
          if (success) {
            toast({
              title: 'Route Completed',
              description:
                'All recalls have been processed. Route automatically marked as completed.'
            });
            // eslint-disable-next-line no-console
            console.log(`[RouteTracker] Auto-transitioned route ${route.routeId} to completed`);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error auto-transitioning route to completed:', err);
        }
      }
    },
    [toast]
  );

  // Check for inconsistent routes on load and auto-transition where needed
  useEffect(() => {
    const checkAndTransitionRoutes = async () => {
      for (const route of routes) {
        // Forward only: a route may finish, but nothing re-opens it.
        await autoTransitionToCompleted(route);
      }
    };
    if (routes.length > 0) {
      checkAndTransitionRoutes();
    }
  }, [routes, autoTransitionToCompleted]);

  // Save all changes
  const saveChanges = useCallback(async () => {
    try {
      setSaving(true);

      // Validate that "Other Reason" recalls have required notes
      const missingNotes = Array.from(enhancedRecalls.entries()).filter(
        ([, recall]) =>
          recall.statusUpdatedAt && recall.finalStatus === 'other' && !recall.statusNotes?.trim()
      );

      if (missingNotes.length > 0) {
        toast({
          variant: 'destructive',
          title: 'Notes Required',
          description:
            'Please enter notes for all recalls with "Other Reason" status before saving.'
        });
        setSaving(false);
        return;
      }

      // Validate that "Other Reason" recalls have required reason selection
      const missingReason = Array.from(enhancedRecalls.entries()).filter(
        ([, recall]) =>
          recall.statusUpdatedAt && recall.finalStatus === 'other' && !recall.otherReason?.trim()
      );

      if (missingReason.length > 0) {
        toast({
          variant: 'destructive',
          title: 'Reason Required',
          description:
            'Please select a reason for all recalls with "Other Reason" status before saving.'
        });
        setSaving(false);
        return;
      }

      // Convert enhanced recalls to update format
      const updates: RecallStatusUpdate[] = Array.from(enhancedRecalls.entries())
        .filter(([, recall]) => recall.statusUpdatedAt)
        .map(([assignmentId, recall]) => {
          // Find technician name if servicedByTechnicianId is provided
          const servicedByTechnician = recall.servicedByTechnicianId
            ? technicians.find(t => t.id === recall.servicedByTechnicianId)
            : null;

          return {
            assignmentId,
            status: recall.finalStatus || 'incomplete',
            reason: recall.otherReason,
            notes: recall.statusNotes,
            servicedByTechnicianId: recall.servicedByTechnicianId,
            servicedByTechnicianName: servicedByTechnician?.name || recall.servicedByTechnicianName,
            completedByUserId: userProfile?.id,
            completedByUserName:
              userProfile?.displayName ||
              (userProfile?.firstName && userProfile?.lastName
                ? `${userProfile.firstName} ${userProfile.lastName}`
                : userProfile?.email)
          };
        });

      // Save the updates
      const result = await routeTrackingAPI.updateRecallStatuses(updates);

      if (result.success) {
        logUserActivity({
          category: 'route_tracking',
          action: 'update_recall_status',
          entityType: 'assignment',
          itemCount: updates.length,
          metadata: { update_count: updates.length }
        });

        setUnsavedChanges(false);
        setIndividualUpdates(new Set()); // Clear individual updates tracking

        // Reload routes FIRST to get fresh database state
        const freshRoutes = await routeTrackingAPI.getRoutesFromTable({
          dateRange: {
            start: format(selectedDate, 'yyyy-MM-dd'),
            end: format(selectedDate, 'yyyy-MM-dd'),
            preset: 'custom'
          }
        });

        // Check if any route should auto-complete (based on fresh database state)
        for (const route of freshRoutes) {
          await autoTransitionToCompleted(route);
        }

        // Reload monthly routes to update UI
        await loadMonthlyRoutes();

        // Invalidate recall list cache so the list page shows updated data
        queryClient.invalidateQueries({ queryKey: ['recalls'] });

        // Completing a recall here changes what the OTHER routing pages should show, and
        // they cache independently. `useTechnicianAssignments` has a 30s staleTime, so
        // without this a dispatcher who navigated straight to the assignment page saw the
        // recall they had just completed still sitting there as work — only a hard
        // refresh cleared it, because that drops the in-memory cache entirely.
        //
        // Invalidate rather than refetch: React Query marks these stale and refetches when
        // the page that owns them actually mounts, so we do not pay for data nobody is
        // looking at.
        queryClient.invalidateQueries({ queryKey: assignmentKeys.all });
        queryClient.invalidateQueries({ queryKey: routeKeys.all });
        queryClient.invalidateQueries({ queryKey: dailyRouteSummaryKeys.all });

        // Clear map caches so completed_recalls and raw data are refreshed on map page
        if (tenantId) {
          removeCache('user_preferences', tenantId);
          removeCache('raw_recalls', tenantId);
        }

        toast({
          title: 'Success',
          description: 'Recall statuses have been updated successfully.'
        });
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to save changes:', result.error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: result.error || 'Failed to update recall statuses.'
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save changes:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred.'
      });
    } finally {
      setSaving(false);
    }
  }, [
    enhancedRecalls,
    individualUpdates,
    routes,
    userProfile,
    technicians,
    loadMonthlyRoutes,
    toast,
    autoTransitionToCompleted,
    selectedDate
  ]);

  // Get route progress
  const getRouteProgress = useCallback(
    (route: RouteTrackerItem) => {
      const total = route.recalls.length;
      const completed = route.recalls.filter(
        recall => enhancedRecalls.get(recall.assignmentId)?.finalStatus
      ).length;
      return { total, completed, percentage: total > 0 ? (completed / total) * 100 : 0 };
    },
    [enhancedRecalls]
  );

  // Get overall statistics for the selected date.
  //
  // Counts UNIQUE recalls (vin|campaignNumber) for the day, matching the
  // semantic of the "Recently Updated" section. The same recall can appear
  // multiple times across routes if it was released and reassigned — we
  // count it once with the most-resolved status.
  //
  // Two-pass dedup:
  //   1. route.recalls (stops) — but skip entries that match the same route's
  //      releasedRecalls (those are the released-snapshot entries). Active
  //      assignments take precedence. If a VIN+campaign appears across
  //      routes, the first non-released entry wins — which is correct because
  //      checkExistingAssignment guarantees only one active assignment per
  //      vin|campaign at a time.
  //   2. releasedRecalls — only add for vin|campaign keys NOT already in the
  //      map. This catches recalls that were released and NOT reassigned.
  //      If a release was followed by a reassignment + completion on another
  //      route, Pass 1 already added the active version with the right status.
  const statistics = useMemo(() => {
    const stats = {
      totalRoutes: routes.length,
      totalRecalls: 0,
      totalVehicles: 0,
      completed: 0,
      vehicleSold: 0,
      notAvailable: 0,
      incomplete: 0,
      other: 0,
      pending: 0
    };

    const uniqueVins = new Set<string>();
    const dedupMap = new Map<string, { vin: string; status: string | undefined }>();

    // Pass 1: active assignments from stops (skip release-snapshot entries
    // AND skip manual stops — they're tallied separately in manualStopStatistics).
    routes.forEach(route => {
      const releasedKeys = new Set(
        (route.releasedRecalls ?? []).map(r => `${r.vin}|${r.campaignNumber ?? ''}`)
      );
      route.recalls.forEach(recall => {
        if (isManualStop(recall)) return;
        const key = `${recall.vin}|${recall.campaignNumber ?? ''}`;
        if (releasedKeys.has(key)) return; // released; will be handled in Pass 2 if needed
        if (dedupMap.has(key)) return;
        const enhanced = enhancedRecalls.get(recall.assignmentId);
        dedupMap.set(key, { vin: recall.vin, status: enhanced?.finalStatus });
      });
    });

    // Pass 2: released entries for vin|campaign keys not already covered by an
    // active assignment (i.e., released and never reassigned). Skip manual stops.
    routes.forEach(route => {
      (route.releasedRecalls ?? []).forEach(released => {
        if (released.stopType === 'manual') return;
        const key = `${released.vin}|${released.campaignNumber ?? ''}`;
        if (dedupMap.has(key)) return; // active assignment exists; release is historical
        dedupMap.set(key, { vin: released.vin, status: released.releaseStatus });
      });
    });

    // Count from the dedup map
    dedupMap.forEach(({ vin, status }) => {
      stats.totalRecalls++;
      if (vin) uniqueVins.add(vin);
      if (!status) {
        stats.pending++;
        return;
      }
      switch (status) {
        case 'completed':
          stats.completed++;
          break;
        case 'vehicle_sold':
          stats.vehicleSold++;
          break;
        case 'not_available':
          stats.notAvailable++;
          break;
        case 'incomplete':
          stats.incomplete++;
          break;
        case 'other':
          stats.other++;
          break;
      }
    });

    stats.totalVehicles = uniqueVins.size;
    return stats;
  }, [routes, enhancedRecalls, isManualStop]);

  // Per-day statistics for manual stops only. Mirror of `statistics` but inverted
  // filter — counts ONLY manual stops with the simpler 2-state status model
  // (completed / incomplete / pending). Drives the new "Manual Stops" hero column
  // in the Date Summary card.
  const manualStopStatistics = useMemo(() => {
    const stats = { totalManualStops: 0, completed: 0, incomplete: 0, pending: 0 };
    const dedupMap = new Map<string, { isActive: boolean; finalStatus: string | undefined }>();

    // Pass 1: active manual stops from stops (skip release-snapshot entries)
    routes.forEach(route => {
      const releasedKeys = new Set(
        (route.releasedRecalls ?? []).map(r => `${r.vin}|${r.campaignNumber ?? ''}`)
      );
      route.recalls.forEach(recall => {
        if (!isManualStop(recall)) return; // INVERTED filter — only manual stops
        const key = `${recall.vin}|${recall.campaignNumber ?? ''}`;
        if (releasedKeys.has(key)) return;
        if (dedupMap.has(key)) return;
        const enhanced = enhancedRecalls.get(recall.assignmentId);
        dedupMap.set(key, { isActive: true, finalStatus: enhanced?.finalStatus });
      });
    });

    // Pass 2: released-only manual stops (rare but possible after auto-unassign)
    routes.forEach(route => {
      (route.releasedRecalls ?? []).forEach(released => {
        if (released.stopType !== 'manual') return; // ONLY manual stops
        const key = `${released.vin}|${released.campaignNumber ?? ''}`;
        if (dedupMap.has(key)) return;
        // Released manual stops map to 'incomplete' in the 2-state model
        // (their releaseStatus is from the recall vocabulary which doesn't apply).
        dedupMap.set(key, { isActive: false, finalStatus: 'incomplete' });
      });
    });

    dedupMap.forEach(({ finalStatus }) => {
      stats.totalManualStops++;
      if (!finalStatus) stats.pending++;
      else if (finalStatus === 'completed') stats.completed++;
      else stats.incomplete++; // anything resolved-but-not-completed → incomplete
    });

    return stats;
  }, [routes, enhancedRecalls, isManualStop]);

  // Monthly statistics across every day in the visible month — recalls only.
  // Manual stops excluded.
  //
  // Counts UNIQUE recalls (vin|campaignNumber) PER DAY to match the day-level
  // statistics' semantic. Same dedup pattern as `statistics`: active assignments
  // take precedence over released entries via two-pass within each day. Same
  // vin|campaign on different days IS counted separately (they're distinct
  // scheduling events).
  const monthlyStatistics = useMemo(() => {
    const stats = {
      totalRecalls: 0,
      completed: 0,
      incomplete: 0,
      vehicleSold: 0,
      notAvailable: 0,
      other: 0,
      notUpdated: 0,
      // Future-dated, not-yet-actionable recalls. These CANNOT be updated yet
      // (status updates are blocked for future routes), so they are tracked
      // separately instead of being counted as "Pending" and dragging down the
      // completion / up-to-date progress numbers.
      scheduled: 0
    };

    monthlyRoutes.forEach((dayRoutes, dateKey) => {
      // A whole day is "future" when its route date is after today — recalls on
      // that day are scheduled but not yet workable.
      const isFuture = isRouteDateInFuture(dateKey);

      // Per-day dedup map (across all routes for the same day)
      const dedupMap = new Map<
        string,
        | { source: 'recall'; recall: (typeof dayRoutes)[number]['recalls'][number] }
        | { source: 'released'; releaseStatus: string }
      >();

      // Pass 1: active recalls from stops (skip release-snapshot entries)
      dayRoutes.forEach(route => {
        const releasedKeys = new Set(
          (route.releasedRecalls ?? []).map(r => `${r.vin}|${r.campaignNumber ?? ''}`)
        );
        route.recalls.forEach(recall => {
          if (isManualStop(recall)) return;
          const key = `${recall.vin}|${recall.campaignNumber ?? ''}`;
          if (releasedKeys.has(key)) return;
          if (dedupMap.has(key)) return;
          dedupMap.set(key, { source: 'recall', recall });
        });
      });

      // Pass 2: released-only recalls
      dayRoutes.forEach(route => {
        (route.releasedRecalls ?? []).forEach(released => {
          const key = `${released.vin}|${released.campaignNumber ?? ''}`;
          if (dedupMap.has(key)) return;
          dedupMap.set(key, { source: 'released', releaseStatus: released.releaseStatus });
        });
      });

      // Tally
      dedupMap.forEach(entry => {
        stats.totalRecalls++;

        if (entry.source === 'released') {
          if (entry.releaseStatus === 'not_available') stats.notAvailable++;
          else if (entry.releaseStatus === 'incomplete') stats.incomplete++;
          else if (entry.releaseStatus === 'other') stats.other++;
          return;
        }

        const recall = entry.recall;
        if (recall.status === 'completed') {
          stats.completed++;
          return;
        }

        if (recall.status === 'cancelled' || recall.status === 'in_progress') {
          const rs = recall.releaseStatus === 'not_there' ? 'not_available' : recall.releaseStatus;
          if (rs === 'not_available') stats.notAvailable++;
          else if (rs === 'incomplete') stats.incomplete++;
          else if (rs === 'vehicle_sold') stats.vehicleSold++;
          else if (rs === 'other') stats.other++;
          else if (recall.status === 'in_progress') stats.incomplete++;
          else if (isFuture) stats.scheduled++;
          else stats.notUpdated++;
          return;
        }

        if (isFuture) stats.scheduled++;
        else stats.notUpdated++;
      });
    });

    // Percentages and progress are measured against ACTIONABLE work only —
    // recalls the team can actually act on this month. Scheduled (future) work
    // is excluded from the denominator so it neither inflates "Pending" nor
    // deflates completion/up-to-date progress.
    const actionable = stats.totalRecalls - stats.scheduled;
    const updated = actionable - stats.notUpdated;
    const pct = (n: number): number => (actionable > 0 ? Math.round((n / actionable) * 100) : 0);

    return {
      ...stats,
      actionable,
      updated,
      updatedPct: pct(updated),
      completedPct: pct(stats.completed),
      incompletePct: pct(stats.incomplete),
      vehicleSoldPct: pct(stats.vehicleSold),
      notAvailablePct: pct(stats.notAvailable),
      otherPct: pct(stats.other),
      notUpdatedPct: pct(stats.notUpdated)
    };
  }, [monthlyRoutes, isManualStop, isRouteDateInFuture]);

  // Monthly statistics for manual stops only — mirror of `monthlyStatistics`
  // but with inverted filter and the simpler 2-state status model. Drives
  // the new "Manual Stops This Month" sibling card.
  const manualStopMonthlyStatistics = useMemo(() => {
    const stats = {
      totalManualStops: 0,
      completed: 0,
      incomplete: 0,
      notUpdated: 0,
      // Future-dated, not-yet-actionable manual stops (see monthlyStatistics).
      scheduled: 0
    };

    monthlyRoutes.forEach((dayRoutes, dateKey) => {
      const isFuture = isRouteDateInFuture(dateKey);
      const dedupMap = new Map<string, { source: 'recall' | 'released'; status?: string }>();

      // Pass 1: active manual stops from stops (skip release-snapshot entries)
      dayRoutes.forEach(route => {
        const releasedKeys = new Set(
          (route.releasedRecalls ?? []).map(r => `${r.vin}|${r.campaignNumber ?? ''}`)
        );
        route.recalls.forEach(recall => {
          if (!isManualStop(recall)) return; // INVERTED filter
          const key = `${recall.vin}|${recall.campaignNumber ?? ''}`;
          if (releasedKeys.has(key)) return;
          if (dedupMap.has(key)) return;
          dedupMap.set(key, { source: 'recall', status: recall.status });
        });
      });

      // Pass 2: released-only manual stops
      dayRoutes.forEach(route => {
        (route.releasedRecalls ?? []).forEach(released => {
          if (released.stopType !== 'manual') return;
          const key = `${released.vin}|${released.campaignNumber ?? ''}`;
          if (dedupMap.has(key)) return;
          dedupMap.set(key, { source: 'released' });
        });
      });

      // Tally with the 2-state model:
      //   completed iff recall.status === 'completed'
      //   incomplete iff explicitly released or status === 'cancelled'/'in_progress'
      //   notUpdated iff still 'assigned' (no action taken yet)
      dedupMap.forEach(entry => {
        stats.totalManualStops++;

        if (entry.source === 'released') {
          stats.incomplete++;
          return;
        }

        if (entry.status === 'completed') {
          stats.completed++;
          return;
        }

        if (entry.status === 'cancelled' || entry.status === 'in_progress') {
          stats.incomplete++;
          return;
        }

        if (isFuture) stats.scheduled++;
        else stats.notUpdated++;
      });
    });

    // Actionable-only denominator, mirroring monthlyStatistics.
    const actionable = stats.totalManualStops - stats.scheduled;
    const updated = actionable - stats.notUpdated;
    const pct = (n: number): number => (actionable > 0 ? Math.round((n / actionable) * 100) : 0);

    return {
      ...stats,
      actionable,
      updated,
      updatedPct: pct(updated),
      completedPct: pct(stats.completed),
      incompletePct: pct(stats.incomplete),
      notUpdatedPct: pct(stats.notUpdated)
    };
  }, [monthlyRoutes, isManualStop, isRouteDateInFuture]);

  // Recall-only mirror of `statistics` for the day. Drives the celebration
  // banner and the bulk-actions warning so they fire based on recall progress
  // alone — manual stops are tracked separately and don't gate these UX cues.
  //
  // Same vin|campaign dedup as `statistics`: a recall released on one route
  // and reassigned/completed on another counts once, with the active state
  // taking precedence over the released state.
  const recallOnlyDayStats = useMemo(() => {
    const dedupMap = new Map<string, { isActive: boolean; finalStatus: string | undefined }>();

    // Pass 1: active assignments
    routes.forEach(route => {
      const releasedKeys = new Set(
        (route.releasedRecalls ?? []).map(r => `${r.vin}|${r.campaignNumber ?? ''}`)
      );
      route.recalls.forEach(recall => {
        if (isManualStop(recall)) return;
        const key = `${recall.vin}|${recall.campaignNumber ?? ''}`;
        if (releasedKeys.has(key)) return;
        if (dedupMap.has(key)) return;
        const enhanced = enhancedRecalls.get(recall.assignmentId);
        dedupMap.set(key, { isActive: true, finalStatus: enhanced?.finalStatus });
      });
    });

    // Pass 2: released-only
    routes.forEach(route => {
      (route.releasedRecalls ?? []).forEach(released => {
        const key = `${released.vin}|${released.campaignNumber ?? ''}`;
        if (dedupMap.has(key)) return;
        // Released recalls are never pending — they have a resolved status.
        dedupMap.set(key, { isActive: false, finalStatus: released.releaseStatus });
      });
    });

    let totalRecalls = 0;
    let pending = 0;
    dedupMap.forEach(({ finalStatus }) => {
      totalRecalls++;
      if (!finalStatus) pending++;
    });
    return { totalRecalls, pending };
  }, [routes, enhancedRecalls, isManualStop]);

  // Manual-stops-only mirror of `recallOnlyDayStats`. Drives the manual-stop
  // pending count in the Bulk Actions warning header and feeds into the
  // celebration banner gate (which now requires BOTH recalls AND manual stops
  // to be resolved before firing).
  const manualStopOnlyDayStats = useMemo(() => {
    const dedupMap = new Map<string, { isActive: boolean; finalStatus: string | undefined }>();

    routes.forEach(route => {
      const releasedKeys = new Set(
        (route.releasedRecalls ?? []).map(r => `${r.vin}|${r.campaignNumber ?? ''}`)
      );
      route.recalls.forEach(recall => {
        if (!isManualStop(recall)) return;
        const key = `${recall.vin}|${recall.campaignNumber ?? ''}`;
        if (releasedKeys.has(key)) return;
        if (dedupMap.has(key)) return;
        const enhanced = enhancedRecalls.get(recall.assignmentId);
        dedupMap.set(key, { isActive: true, finalStatus: enhanced?.finalStatus });
      });
    });

    routes.forEach(route => {
      (route.releasedRecalls ?? []).forEach(released => {
        if (released.stopType !== 'manual') return;
        const key = `${released.vin}|${released.campaignNumber ?? ''}`;
        if (dedupMap.has(key)) return;
        dedupMap.set(key, { isActive: false, finalStatus: 'incomplete' });
      });
    });

    let totalManualStops = 0;
    let pending = 0;
    dedupMap.forEach(({ finalStatus }) => {
      totalManualStops++;
      if (!finalStatus) pending++;
    });
    return { totalManualStops, pending };
  }, [routes, enhancedRecalls, isManualStop]);

  // Audit logging: Log data access when routes are loaded
  useEffect(() => {
    if (routes.length > 0 && tenantId) {
      const totalRecalls = routes.reduce((sum, route) => sum + route.recalls.length, 0);
      logDataAccess({
        resource: 'route_tracker',
        action: 'view',
        resourceCount: routes.length,
        metadata: {
          selectedDate: format(selectedDate, 'yyyy-MM-dd'),
          routeCount: routes.length,
          totalRecalls,
          tenantId
        }
      });
    }
    // Only log once per routes change to avoid spam
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes.length, selectedDate]);

  return (
    <SecurityBoundary
      requiredPermissions={[{ resource: 'recalls', action: 'read', scope: PermissionScope.SELF }]}
      auditPageAccess='route-tracker'
      tenantIsolation={true}
    >
      <div className='container mx-auto pt-2 px-4 md:px-6 pb-4 max-w-7xl'>
        {/* Action bar (page title removed — the left nav labels the page) */}
        <div className='flex items-center justify-end gap-4 mb-6'>
          <div className='flex gap-2'>
            {unsavedChanges && (canUpdateAnyRecallStatus || canUpdateOwnRecallStatus) && (
              <Button onClick={saveChanges} disabled={saving} className='gap-2'>
                {saving ? (
                  <RefreshCw className='h-4 w-4 animate-spin' />
                ) : (
                  <Save className='h-4 w-4' />
                )}
                Save Changes
              </Button>
            )}
            <Button variant='outline' onClick={loadMonthlyRoutes} disabled={loading}>
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            </Button>
          </div>
        </div>

        {/* Info banner for technicians - they can only update their own routes */}
        {canUpdateOwnRecallStatus && !canUpdateAnyRecallStatus && linkedTechnicianId && (
          <Alert className='mb-6 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20'>
            <Info className='h-4 w-4' />
            <AlertTitle>Your Routes</AlertTitle>
            <AlertDescription>
              You can update statuses for recalls assigned to you.
            </AlertDescription>
          </Alert>
        )}

        {/* Main Layout — on lg+ each column scrolls independently so the
            calendar + analytics column stays visible while the right column
            scrolls through routes. Mobile (<lg) keeps page-level scrolling. */}
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-6 lg:h-[calc(100vh-160px)]'>
          {/* Left Column - Calendar */}
          <div className='lg:col-span-4 lg:h-[calc(100vh-160px)] lg:overflow-y-auto lg:pr-1'>
            <Card>
              <CardHeader className='py-3'>
                <div className='flex items-center justify-between'>
                  <CardTitle className='text-sm font-semibold'>Select Date</CardTitle>
                  <div className='flex items-center gap-1'>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-7 w-7'
                      onClick={() =>
                        setCurrentMonth(
                          new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
                        )
                      }
                    >
                      <ChevronLeft className='h-4 w-4' />
                    </Button>
                    <span className='text-xs font-medium px-2'>
                      {format(currentMonth, 'MMMM yyyy')}
                    </span>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-7 w-7'
                      onClick={() =>
                        setCurrentMonth(
                          new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
                        )
                      }
                    >
                      <ChevronRightIcon className='h-4 w-4' />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className='p-4 pt-0'>
                <div className='grid grid-cols-7 gap-1'>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div
                      key={day}
                      className='text-center text-xs font-medium text-muted-foreground py-1'
                    >
                      {day}
                    </div>
                  ))}
                </div>
                <div className='grid grid-cols-7 gap-1'>
                  {/* Empty cells for days before the first of the month */}
                  {Array.from({ length: startOfMonth(currentMonth).getDay() }).map((_, index) => {
                    const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
                    return <div key={`empty-${dayNames[index]}`} className='h-9' />;
                  })}
                  {calendarDays.map(dayStatus => {
                    const isSelected = isSameDay(dayStatus.date, selectedDate);
                    const isCurrentDay = isToday(dayStatus.date);

                    return (
                      <TooltipProvider key={`day-${dayStatus.date.getTime()}`}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant={isSelected ? 'default' : 'ghost'}
                              className={cn(
                                'h-9 w-full p-0 font-normal relative',
                                isCurrentDay && !isSelected && 'border-2 border-primary',
                                !dayStatus.hasRoutes && 'text-muted-foreground'
                              )}
                              onClick={() => setSelectedDate(dayStatus.date)}
                            >
                              <div className='flex flex-col items-center justify-center w-full h-full'>
                                <span className='text-sm leading-none'>
                                  {format(dayStatus.date, 'd')}
                                </span>
                                {dayStatus.hasRoutes && (
                                  <div className='flex gap-0.5 mt-0.5'>
                                    {dayStatus.hasPendingRecalls ? (
                                      <Circle className='h-1.5 w-1.5 fill-orange-500 text-orange-500' />
                                    ) : (
                                      <Circle className='h-1.5 w-1.5 fill-green-500 text-green-500' />
                                    )}
                                  </div>
                                )}
                              </div>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {dayStatus.hasRoutes ? (
                              <div className='text-xs'>
                                <p>{dayStatus.totalRecalls} recalls</p>
                                <p>{dayStatus.completedRecalls} completed</p>
                                {dayStatus.hasPendingRecalls && (
                                  <p className='text-orange-400'>Pending updates</p>
                                )}
                              </div>
                            ) : (
                              <p className='text-xs'>No routes</p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                </div>

                {/* Legend — single inline row, half the height of the stacked version */}
                <div className='mt-3 flex items-center gap-4 text-xs'>
                  <span className='flex items-center gap-1.5'>
                    <Circle className='h-2.5 w-2.5 fill-orange-500 text-orange-500' />
                    <span className='text-muted-foreground'>Pending</span>
                  </span>
                  <span className='flex items-center gap-1.5'>
                    <Circle className='h-2.5 w-2.5 fill-green-500 text-green-500' />
                    <span className='text-muted-foreground'>All updated</span>
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Date Summary — selected day stats, compact hero layout */}
            {routes.length > 0 && (
              <Card className='mt-4'>
                <CardHeader className='py-3'>
                  <CardTitle className='text-sm font-semibold'>
                    {format(selectedDate, 'EEEE, MMMM d')}
                  </CardTitle>
                </CardHeader>
                <CardContent className='p-4 pt-0'>
                  {/* Hero stat row — big numbers first, labels below.
                      Manual Stops column appears only when the day has any,
                      keeping the grid balanced for recall-only days. */}
                  <div
                    className={cn(
                      'grid gap-2 text-center',
                      manualStopStatistics.totalManualStops > 0 ? 'grid-cols-4' : 'grid-cols-3'
                    )}
                  >
                    <div>
                      <div className='text-2xl font-bold leading-none tabular-nums'>
                        {statistics.totalRoutes}
                      </div>
                      <div className='text-xs text-muted-foreground mt-1.5'>Routes</div>
                    </div>
                    <div>
                      <div className='text-2xl font-bold leading-none tabular-nums'>
                        {statistics.totalVehicles}
                      </div>
                      <div className='text-xs text-muted-foreground mt-1.5'>Vehicles</div>
                    </div>
                    <div>
                      <div className='text-2xl font-bold leading-none tabular-nums'>
                        {statistics.totalRecalls}
                      </div>
                      <div className='text-xs text-muted-foreground mt-1.5'>Recalls</div>
                    </div>
                    {manualStopStatistics.totalManualStops > 0 && (
                      <div>
                        <div className='text-2xl font-bold leading-none tabular-nums'>
                          {manualStopStatistics.totalManualStops}
                        </div>
                        <div className='text-xs text-muted-foreground mt-1.5'>Manual Stops</div>
                      </div>
                    )}
                  </div>
                  {/* Inline pending count OR all-updated success state.
                      Replaces the previous 3-row vertical layout + standalone
                      Action Required Alert (which duplicated the Pending count
                      and added vertical noise without new info).
                      Pending = recalls + manual stops awaiting status update,
                      matching how Bulk Actions, the Pending tab, and the
                      completion banner gate compute it. (statistics.pending is
                      the recall-only count, used elsewhere for recall-specific
                      breakdowns; the Date Summary needs the combined total.) */}
                  {recallOnlyDayStats.pending + manualStopOnlyDayStats.pending > 0 ? (
                    <div className='mt-3 flex items-center justify-between text-sm'>
                      <span className='flex items-center gap-2'>
                        <AlertCircle className='h-4 w-4 text-orange-500' />
                        <span className='text-muted-foreground'>Pending</span>
                      </span>
                      <span className='font-semibold tabular-nums'>
                        {recallOnlyDayStats.pending + manualStopOnlyDayStats.pending}
                      </span>
                    </div>
                  ) : (
                    <div
                      className={cn(
                        'mt-3 flex items-center gap-2 rounded-md px-3 py-2',
                        'bg-green-50 dark:bg-green-950/30',
                        'border border-green-200 dark:border-green-800'
                      )}
                    >
                      <CheckCircle2 className='h-4 w-4 text-green-600 dark:text-green-400' />
                      <span className='text-sm font-medium text-green-800 dark:text-green-300'>
                        All statuses updated
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Monthly Stats Card — month-to-date recall outcomes (recalls only) */}
            <Card className='mt-4'>
              <CardHeader className='py-3'>
                <div className='flex items-center justify-between'>
                  <CardTitle className='text-sm font-semibold'>This Month</CardTitle>
                  <span className='text-xs text-muted-foreground'>
                    {format(currentMonth, 'MMMM yyyy')}
                  </span>
                </div>
              </CardHeader>
              <CardContent className='p-4 pt-0'>
                {monthlyStatistics.totalRecalls === 0 &&
                manualStopMonthlyStatistics.totalManualStops === 0 ? (
                  <div className='text-center py-4'>
                    <Car className='h-6 w-6 mx-auto mb-2 text-muted-foreground opacity-50' />
                    <p className='text-xs text-muted-foreground'>Nothing scheduled this month</p>
                  </div>
                ) : (
                  <>
                    {monthlyStatistics.totalRecalls > 0 && (
                      <>
                        {/* Vehix Recalls section header — tinted pill, left-aligned */}
                        <div className='mb-2'>
                          <span className='inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-orange-50 dark:bg-orange-950/40'>
                            <Wrench className='h-4 w-4 text-orange-600 dark:text-orange-400 flex-shrink-0' />
                            <span className='text-xs font-bold uppercase tracking-wider text-orange-700 dark:text-orange-300'>
                              Vehix Recalls
                            </span>
                          </span>
                        </div>
                        {monthlyStatistics.actionable > 0 ? (
                          /* Horizontal layout — donut left, breakdown right */
                          <div className='flex items-center gap-3'>
                            {(() => {
                              const slices = [
                                {
                                  name: 'Completed',
                                  value: monthlyStatistics.completed,
                                  color: '#16a34a'
                                },
                                {
                                  name: 'Incomplete',
                                  value: monthlyStatistics.incomplete,
                                  color: '#2563eb'
                                },
                                {
                                  name: 'Vehicle Sold',
                                  value: monthlyStatistics.vehicleSold,
                                  color: '#dc2626'
                                },
                                {
                                  name: 'Not Available',
                                  value: monthlyStatistics.notAvailable,
                                  color: '#ea580c'
                                },
                                {
                                  name: 'Other',
                                  value: monthlyStatistics.other,
                                  color: '#6b7280'
                                },
                                {
                                  name: 'Not Updated',
                                  value: monthlyStatistics.notUpdated,
                                  color: '#f59e0b'
                                }
                              ].filter(slice => slice.value > 0);

                              const total = monthlyStatistics.actionable;

                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              const DonutTooltip = ({ active, payload }: any) => {
                                if (!active || !payload?.length) return null;
                                const { name, value } = payload[0];
                                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                                return (
                                  <div className='bg-background border border-border rounded-lg shadow-lg px-3 py-2'>
                                    <div className='flex items-center gap-2 text-sm'>
                                      <div
                                        className='w-2.5 h-2.5 rounded-full flex-shrink-0'
                                        style={{ backgroundColor: payload[0].payload.color }}
                                      />
                                      <span className='text-muted-foreground'>{name}</span>
                                      <span className='font-semibold tabular-nums'>{value}</span>
                                      <span className='text-muted-foreground'>({pct}%)</span>
                                    </div>
                                  </div>
                                );
                              };

                              return (
                                <div
                                  className='relative flex-shrink-0'
                                  style={{ width: 120, height: 120 }}
                                >
                                  <ResponsiveContainer>
                                    <PieChart>
                                      <Pie
                                        data={slices}
                                        dataKey='value'
                                        nameKey='name'
                                        cx='50%'
                                        cy='50%'
                                        innerRadius='64%'
                                        outerRadius='94%'
                                        paddingAngle={2}
                                        stroke='none'
                                        isAnimationActive={false}
                                      >
                                        {slices.map(slice => (
                                          <Cell key={slice.name} fill={slice.color} />
                                        ))}
                                      </Pie>
                                      <RechartsTooltip
                                        content={<DonutTooltip />}
                                        wrapperStyle={{ zIndex: 20 }}
                                      />
                                    </PieChart>
                                  </ResponsiveContainer>
                                  <div
                                    className={cn(
                                      'absolute inset-0 flex flex-col items-center justify-center',
                                      'pointer-events-none z-0'
                                    )}
                                  >
                                    <span className='text-2xl font-bold leading-none tabular-nums'>
                                      {monthlyStatistics.actionable}
                                    </span>
                                    <span className='text-xs text-muted-foreground mt-0.5'>
                                      recalls
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Breakdown list — six rows, recall outcomes with share-of-total */}
                            <div className='flex-1 min-w-0 space-y-1 text-xs'>
                              <div className='flex items-center justify-between'>
                                <span className='flex items-center gap-2 truncate'>
                                  <CheckCircle2 className='h-3.5 w-3.5 text-green-600 flex-shrink-0' />
                                  Completed
                                </span>
                                <span className='font-semibold tabular-nums'>
                                  {monthlyStatistics.completed}{' '}
                                  <span className='font-normal text-muted-foreground'>
                                    ({monthlyStatistics.completedPct}%)
                                  </span>
                                </span>
                              </div>
                              <div className='flex items-center justify-between'>
                                <span className='flex items-center gap-2 truncate'>
                                  <Clock className='h-3.5 w-3.5 text-blue-600 flex-shrink-0' />
                                  Incomplete
                                </span>
                                <span className='font-semibold tabular-nums'>
                                  {monthlyStatistics.incomplete}{' '}
                                  <span className='font-normal text-muted-foreground'>
                                    ({monthlyStatistics.incompletePct}%)
                                  </span>
                                </span>
                              </div>
                              <div className='flex items-center justify-between'>
                                <span className='flex items-center gap-2 truncate'>
                                  <DollarSign className='h-3.5 w-3.5 text-red-600 flex-shrink-0' />
                                  Sold
                                </span>
                                <span className='font-semibold tabular-nums'>
                                  {monthlyStatistics.vehicleSold}{' '}
                                  <span className='font-normal text-muted-foreground'>
                                    ({monthlyStatistics.vehicleSoldPct}%)
                                  </span>
                                </span>
                              </div>
                              <div className='flex items-center justify-between'>
                                <span className='flex items-center gap-2 truncate'>
                                  <MapPinOff className='h-3.5 w-3.5 text-orange-600 flex-shrink-0' />
                                  Not Avail.
                                </span>
                                <span className='font-semibold tabular-nums'>
                                  {monthlyStatistics.notAvailable}{' '}
                                  <span className='font-normal text-muted-foreground'>
                                    ({monthlyStatistics.notAvailablePct}%)
                                  </span>
                                </span>
                              </div>
                              <div className='flex items-center justify-between'>
                                <span className='flex items-center gap-2 truncate'>
                                  <FileText className='h-3.5 w-3.5 text-gray-500 flex-shrink-0' />
                                  Other
                                </span>
                                <span className='font-semibold tabular-nums'>
                                  {monthlyStatistics.other}{' '}
                                  <span className='font-normal text-muted-foreground'>
                                    ({monthlyStatistics.otherPct}%)
                                  </span>
                                </span>
                              </div>
                              <div className='flex items-center justify-between'>
                                <span className='flex items-center gap-2 truncate'>
                                  <AlertCircle className='h-3.5 w-3.5 text-amber-500 flex-shrink-0' />
                                  Pending
                                </span>
                                <span className='font-semibold tabular-nums'>
                                  {monthlyStatistics.notUpdated}{' '}
                                  <span className='font-normal text-muted-foreground'>
                                    ({monthlyStatistics.notUpdatedPct}%)
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* All-future month — nothing actionable yet */
                          <div className='flex flex-col items-center text-center py-3'>
                            <CalendarClock className='h-6 w-6 mb-1 text-muted-foreground opacity-60' />
                            <p className='text-xs text-muted-foreground'>
                              Nothing to work yet — {monthlyStatistics.scheduled} scheduled for
                              upcoming days
                            </p>
                          </div>
                        )}
                        {/* Scheduled (future) recalls — tracked apart from Pending
                            since they cannot be updated until their route date
                            arrives. Count only; deliberately outside the
                            actionable percentages. */}
                        {monthlyStatistics.actionable > 0 && monthlyStatistics.scheduled > 0 && (
                          <div className='mt-2 pt-2 border-t border-border flex items-center justify-between text-xs'>
                            <span className='flex items-center gap-2 truncate text-muted-foreground'>
                              <CalendarClock className='h-3.5 w-3.5 flex-shrink-0' />
                              Scheduled
                            </span>
                            <span className='font-semibold tabular-nums text-muted-foreground'>
                              {monthlyStatistics.scheduled}{' '}
                              <span className='font-normal'>upcoming</span>
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {/* Manual Stops mini-donut — mirrors the Recalls donut+breakdown
                        layout but at 80px to stay compact (manual stops have only
                        3 status buckets so the smaller donut paired with 3 rows
                        keeps the card from forcing scroll). */}
                    {manualStopMonthlyStatistics.totalManualStops > 0 && (
                      <div
                        className={cn(
                          monthlyStatistics.totalRecalls > 0 && 'mt-3 pt-3 border-t border-border'
                        )}
                      >
                        {/* Manual Stops section header — tinted pill, left-aligned */}
                        <div className='mb-2'>
                          <span className='inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-blue-50 dark:bg-blue-950/40'>
                            <FileText className='h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0' />
                            <span className='text-xs font-bold uppercase tracking-wider text-blue-700 dark:text-blue-300'>
                              Manual Stops
                            </span>
                          </span>
                        </div>
                        {manualStopMonthlyStatistics.actionable > 0 ? (
                          <div className='flex items-center gap-3'>
                            {(() => {
                              const slices = [
                                {
                                  name: 'Completed',
                                  value: manualStopMonthlyStatistics.completed,
                                  color: '#16a34a'
                                },
                                {
                                  name: 'Incomplete',
                                  value: manualStopMonthlyStatistics.incomplete,
                                  color: '#dc2626'
                                },
                                {
                                  name: 'Pending',
                                  value: manualStopMonthlyStatistics.notUpdated,
                                  color: '#f59e0b'
                                }
                              ].filter(slice => slice.value > 0);

                              const total = manualStopMonthlyStatistics.actionable;

                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              const ManualDonutTooltip = ({ active, payload }: any) => {
                                if (!active || !payload?.length) return null;
                                const { name, value } = payload[0];
                                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                                return (
                                  <div className='bg-background border border-border rounded-lg shadow-lg px-3 py-2'>
                                    <div className='flex items-center gap-2 text-sm'>
                                      <div
                                        className='w-2.5 h-2.5 rounded-full flex-shrink-0'
                                        style={{
                                          backgroundColor: payload[0].payload.color
                                        }}
                                      />
                                      <span className='text-muted-foreground'>{name}</span>
                                      <span className='font-semibold tabular-nums'>{value}</span>
                                      <span className='text-muted-foreground'>({pct}%)</span>
                                    </div>
                                  </div>
                                );
                              };

                              return (
                                // Outer slot matches the Recalls donut wrapper width (120px)
                                // so both donut centers and both breakdown columns line up
                                // on the same vertical axes. Inner donut stays visually 80×80.
                                <div
                                  className='flex items-center justify-center flex-shrink-0'
                                  style={{ width: 120 }}
                                >
                                  <div className='relative' style={{ width: 80, height: 80 }}>
                                    <ResponsiveContainer>
                                      <PieChart>
                                        <Pie
                                          data={slices}
                                          dataKey='value'
                                          nameKey='name'
                                          cx='50%'
                                          cy='50%'
                                          innerRadius='64%'
                                          outerRadius='94%'
                                          paddingAngle={2}
                                          stroke='none'
                                          isAnimationActive={false}
                                        >
                                          {slices.map(slice => (
                                            <Cell key={slice.name} fill={slice.color} />
                                          ))}
                                        </Pie>
                                        <RechartsTooltip
                                          content={<ManualDonutTooltip />}
                                          wrapperStyle={{ zIndex: 20 }}
                                        />
                                      </PieChart>
                                    </ResponsiveContainer>
                                    <div
                                      className={cn(
                                        'absolute inset-0 flex flex-col items-center justify-center',
                                        'pointer-events-none z-0'
                                      )}
                                    >
                                      <span className='text-xl font-bold leading-none tabular-nums'>
                                        {total}
                                      </span>
                                      <span className='text-[10px] text-muted-foreground mt-0.5'>
                                        stops
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Manual stops breakdown — three rows mirroring Recalls layout */}
                            <div className='flex-1 min-w-0 space-y-1 text-xs'>
                              <div className='flex items-center justify-between'>
                                <span className='flex items-center gap-2 truncate'>
                                  <CheckCircle2 className='h-3.5 w-3.5 text-green-600 flex-shrink-0' />
                                  Completed
                                </span>
                                <span className='font-semibold tabular-nums'>
                                  {manualStopMonthlyStatistics.completed}{' '}
                                  <span className='font-normal text-muted-foreground'>
                                    ({manualStopMonthlyStatistics.completedPct}%)
                                  </span>
                                </span>
                              </div>
                              <div className='flex items-center justify-between'>
                                <span className='flex items-center gap-2 truncate'>
                                  <Clock className='h-3.5 w-3.5 text-red-600 flex-shrink-0' />
                                  Incomplete
                                </span>
                                <span className='font-semibold tabular-nums'>
                                  {manualStopMonthlyStatistics.incomplete}{' '}
                                  <span className='font-normal text-muted-foreground'>
                                    ({manualStopMonthlyStatistics.incompletePct}%)
                                  </span>
                                </span>
                              </div>
                              <div className='flex items-center justify-between'>
                                <span className='flex items-center gap-2 truncate'>
                                  <AlertCircle className='h-3.5 w-3.5 text-amber-500 flex-shrink-0' />
                                  Pending
                                </span>
                                <span className='font-semibold tabular-nums'>
                                  {manualStopMonthlyStatistics.notUpdated}{' '}
                                  <span className='font-normal text-muted-foreground'>
                                    ({manualStopMonthlyStatistics.notUpdatedPct}%)
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* All-future month — nothing actionable yet */
                          <div className='flex flex-col items-center text-center py-3'>
                            <CalendarClock className='h-6 w-6 mb-1 text-muted-foreground opacity-60' />
                            <p className='text-xs text-muted-foreground'>
                              Nothing to work yet — {manualStopMonthlyStatistics.scheduled}{' '}
                              scheduled for upcoming days
                            </p>
                          </div>
                        )}
                        {/* Scheduled (future) manual stops — count only, outside
                            the actionable percentages (mirrors recalls). */}
                        {manualStopMonthlyStatistics.actionable > 0 &&
                          manualStopMonthlyStatistics.scheduled > 0 && (
                            <div className='mt-2 pt-2 border-t border-border flex items-center justify-between text-xs'>
                              <span className='flex items-center gap-2 truncate text-muted-foreground'>
                                <CalendarClock className='h-3.5 w-3.5 flex-shrink-0' />
                                Scheduled
                              </span>
                              <span className='font-semibold tabular-nums text-muted-foreground'>
                                {manualStopMonthlyStatistics.scheduled}{' '}
                                <span className='font-normal'>upcoming</span>
                              </span>
                            </div>
                          )}
                      </div>
                    )}

                    {/* Combined Updated progress bar — reflects BOTH recalls
                        and manual stops. Single headline number for "all-work
                        progress this month" instead of separate per-type bars. */}
                    {(() => {
                      // Progress reflects ACTIONABLE work only — future scheduled
                      // stops are excluded so the bar isn't deflated by work that
                      // can't be updated yet.
                      const total =
                        monthlyStatistics.actionable + manualStopMonthlyStatistics.actionable;
                      const updated =
                        monthlyStatistics.updated + manualStopMonthlyStatistics.updated;
                      // Nothing actionable yet (e.g. an all-future month) — the
                      // per-section "scheduled" states already convey the status.
                      if (total === 0) return null;
                      const pct = total > 0 ? Math.round((updated / total) * 100) : 0;
                      return (
                        <div className='mt-3 space-y-1'>
                          <div className='flex items-center justify-between text-xs'>
                            <span className='text-muted-foreground'>Updated</span>
                            <span className='font-semibold tabular-nums'>
                              {updated} / {total} · {pct}%
                            </span>
                          </div>
                          <Progress value={pct} className='h-1.5' />
                        </div>
                      );
                    })()}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Routes and Recalls */}
          <div className='lg:col-span-8 lg:h-[calc(100vh-160px)] lg:overflow-y-auto lg:pr-1'>
            {loading ? (
              <Card>
                <CardContent className='pt-6'>
                  <div className='flex items-center justify-center py-8'>
                    <RefreshCw className='h-8 w-8 animate-spin text-muted-foreground' />
                  </div>
                </CardContent>
              </Card>
            ) : routes.length === 0 ? (
              <Card>
                <CardContent className='pt-6'>
                  <div className='text-center py-8'>
                    <Car className='h-12 w-12 mx-auto mb-4 text-muted-foreground' />
                    <p className='text-muted-foreground'>
                      No routes scheduled for {format(selectedDate, 'MMMM d, yyyy')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className='space-y-4'>
                {/* TOP section header — frames the action surface (bulk actions + per-route
                    cards with pending recalls). The count reflects all stops still needing
                    a status update (recall stops + manual stops). */}
                <SectionHeader
                  title='Needs Attention'
                  count={recallOnlyDayStats.pending + manualStopOnlyDayStats.pending}
                  countLabel='pending'
                />

                {/* Completion banner — fires when EVERY stop (recall + manual)
                    for the day has a final status. Both must be resolved for the
                    day to be considered "done". Copy adapts to which types are
                    present: recall-only / manual-only / mixed. */}
                {recallOnlyDayStats.pending === 0 &&
                  manualStopOnlyDayStats.pending === 0 &&
                  recallOnlyDayStats.totalRecalls + manualStopOnlyDayStats.totalManualStops > 0 && (
                    <Card
                      className={cn(
                        'border-green-300 dark:border-green-700',
                        'bg-green-50/50 dark:bg-green-950/20'
                      )}
                    >
                      <CardContent className='pt-4 pb-4'>
                        <div className='flex items-center gap-3'>
                          <div
                            className={cn(
                              'h-10 w-10 rounded-full flex items-center justify-center',
                              'bg-green-100 dark:bg-green-900/40'
                            )}
                          >
                            <CheckCircle2 className='h-5 w-5 text-green-600 dark:text-green-400' />
                          </div>
                          <div>
                            <p className='text-sm font-semibold text-green-800 dark:text-green-300'>
                              {(() => {
                                const r = recallOnlyDayStats.totalRecalls;
                                const m = manualStopOnlyDayStats.totalManualStops;
                                const recallWord = r === 1 ? 'recall' : 'recalls';
                                const stopWord = m === 1 ? 'stop' : 'stops';
                                if (r > 0 && m > 0) {
                                  return `All ${r + m} stops updated (${r} ${recallWord} + ${m} manual)`;
                                }
                                if (m > 0) {
                                  return `All ${m} manual ${stopWord} updated for this day`;
                                }
                                return `All ${r} ${recallWord} updated for this day`;
                              })()}
                            </p>
                            <p className='text-xs text-green-700/70 dark:text-green-400/70 mt-0.5'>
                              Every stop has a final status — nice work.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                {/* Bulk Actions - only show if there are pending recalls and user can edit.
                    Gated by recallOnlyDayStats.pending because RECALL_STATUSES doesn't apply
                    to manual stops (different status model). The warning header surfaces
                    manual-stop pending count too, so users see all outstanding work even
                    though the bulk picker only operates on recalls. */}
                {recallOnlyDayStats.pending > 0 && hasEditableRoutes && (
                  <Card className='border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20'>
                    <CardContent className='pt-4'>
                      <div className='flex items-center gap-2 mb-3'>
                        <AlertCircle className='h-4 w-4 text-amber-600 dark:text-amber-400' />
                        <span className='text-sm font-semibold text-amber-800 dark:text-amber-300'>
                          {(() => {
                            const r = recallOnlyDayStats.pending;
                            const m = manualStopOnlyDayStats.pending;
                            const recallText = `${r} ${r === 1 ? 'recall' : 'recalls'}`;
                            if (m > 0) {
                              const manualText = `${m} manual ${m === 1 ? 'stop' : 'stops'}`;
                              return `${recallText} + ${manualText} need updates`;
                            }
                            return `${recallText} ${r === 1 ? 'needs' : 'need'} a status update`;
                          })()}
                        </span>
                      </div>
                      <div className='flex items-center gap-2 flex-wrap'>
                        <Label className='text-sm'>Bulk Actions:</Label>
                        <Button size='sm' variant='outline' onClick={handleSelectAll}>
                          Select All
                        </Button>
                        <Select value={bulkStatus} onValueChange={setBulkStatus}>
                          <SelectTrigger className='w-[180px] h-8'>
                            <SelectValue placeholder='Select status' />
                          </SelectTrigger>
                          <SelectContent>
                            {RECALL_STATUSES.map(status => (
                              <SelectItem key={status.value} value={status.value}>
                                <div className='flex items-center gap-2'>
                                  <status.icon className={cn('h-4 w-4', status.color)} />
                                  {status.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size='sm'
                          onClick={applyBulkStatus}
                          disabled={!bulkStatus || selectedRecalls.size === 0}
                        >
                          Apply to {selectedRecalls.size} selected
                        </Button>
                        {selectedRecalls.size > 0 && (
                          <Button
                            size='sm'
                            variant='ghost'
                            onClick={() => setSelectedRecalls(new Set())}
                          >
                            Clear selection
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Routes - only show Draft and In Progress (completed appear in section below) */}
                {routes
                  .filter(route => route.status !== 'completed')
                  .map(route => {
                    const progress = getRouteProgress(route);
                    const isExpanded = expandedRoutes.has(route.routeId);
                    const isRouteEditable = canEditRoute(route);
                    const pendingInRoute = route.recalls.filter(recall => {
                      const enhanced = enhancedRecalls.get(recall.assignmentId);
                      return !enhanced?.finalStatus || !!enhanced?.statusUpdatedAt;
                    }).length;
                    // route.totalRecalls counts ALL stops (recalls + manual stops) — split it so the
                    // header labels match reality. dealerCount already unions both stop types.
                    const recallCount = route.recalls.filter(r => !isManualStop(r)).length;
                    const manualStopCount = route.recalls.filter(r => isManualStop(r)).length;
                    return (
                      <Card
                        key={route.routeId}
                        className={cn(
                          'border-l-4',
                          progress.percentage === 100 ? 'border-l-green-500' : 'border-l-amber-400'
                        )}
                      >
                        <CardHeader className='pb-3'>
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-3 flex-1'>
                              <div className='h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center'>
                                <span className='text-sm font-semibold text-primary'>
                                  {route.technicianInitials}
                                </span>
                              </div>
                              <div className='flex-1'>
                                <h3 className='font-semibold flex items-center gap-2'>
                                  {route.technicianName}
                                  <Badge variant='secondary' className='text-xs'>
                                    Route {route.routeSequence || 1}
                                  </Badge>
                                </h3>
                                <div className='flex items-center gap-4 text-xs text-muted-foreground mt-1'>
                                  <span className='flex items-center gap-1'>
                                    <Car className='h-3 w-3' />
                                    {recallCount} recalls
                                  </span>
                                  {manualStopCount > 0 && (
                                    <span className='flex items-center gap-1'>
                                      <FileText className='h-3 w-3' />
                                      {manualStopCount} manual stops
                                    </span>
                                  )}
                                  <span className='flex items-center gap-1'>
                                    <MapPin className='h-3 w-3' />
                                    {route.dealerCount} dealers
                                  </span>
                                  {pendingInRoute > 0 && (
                                    <Badge variant='destructive' className='text-xs'>
                                      {pendingInRoute} pending
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className='flex items-center gap-2'>
                              {/* Daily summary entry-point was relocated to the per-technician
                                  card in the Status Updates section below — single entry point
                                  per tech, anchored next to the review surface where it belongs. */}
                              {/* Route Status Badge (read-only, auto-managed) */}
                              <Badge
                                variant='outline'
                                className={cn(
                                  'h-8 px-3 text-xs font-medium',
                                  route.status === 'completed' &&
                                    'bg-green-100 text-green-700 border-green-300 ' +
                                      'dark:bg-green-900/30 dark:text-green-400 dark:border-green-700',
                                  route.status === 'in_progress' &&
                                    'bg-orange-100 text-orange-700 border-orange-300 ' +
                                      'dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700',
                                  (route.status === 'pending' || route.status === 'active') &&
                                    'bg-gray-100 text-gray-600 border-gray-300 ' +
                                      'dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600'
                                )}
                              >
                                <span className='flex items-center gap-2'>
                                  {route.status === 'completed' && (
                                    <CheckCircle2 className='h-3 w-3' />
                                  )}
                                  {route.status === 'in_progress' && <Clock className='h-3 w-3' />}
                                  {(route.status === 'pending' || route.status === 'active') && (
                                    <Circle className='h-3 w-3' />
                                  )}
                                  {route.status === 'completed'
                                    ? 'Completed'
                                    : route.status === 'in_progress'
                                      ? 'In Progress'
                                      : 'Draft'}
                                </span>
                              </Badge>
                              {route.status !== 'completed' && (
                                <Collapsible
                                  open={isExpanded}
                                  onOpenChange={open => {
                                    setExpandedRoutes(prev => {
                                      const newSet = new Set(prev);
                                      if (open) {
                                        newSet.add(route.routeId);
                                      } else {
                                        newSet.delete(route.routeId);
                                      }
                                      return newSet;
                                    });
                                  }}
                                >
                                  <CollapsibleTrigger asChild>
                                    <Button variant='ghost' size='icon'>
                                      {isExpanded ? (
                                        <ChevronDown className='h-4 w-4' />
                                      ) : (
                                        <ChevronRight className='h-4 w-4' />
                                      )}
                                    </Button>
                                  </CollapsibleTrigger>
                                </Collapsible>
                              )}
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className='mt-3 space-y-1'>
                            <div className='flex justify-between text-xs text-muted-foreground'>
                              <span>
                                {progress.completed} of {progress.total} completed
                              </span>
                              <span>{Math.round(progress.percentage)}%</span>
                            </div>
                            <Progress
                              value={progress.percentage}
                              className={cn(
                                'h-2',
                                progress.percentage === 100
                                  ? '[&>div]:bg-green-500'
                                  : '[&>div]:bg-amber-500'
                              )}
                            />
                          </div>
                        </CardHeader>

                        {route.status !== 'completed' && (
                          <Collapsible open={isExpanded}>
                            <CollapsibleContent>
                              <CardContent className='pt-0'>
                                <Separator className='mb-4' />
                                <div className='space-y-6'>
                                  {(() => {
                                    // Separate recalls into pending and completed
                                    // Pending recalls: no finalStatus OR has unsaved changes (statusUpdatedAt)
                                    const pendingRecalls = route.recalls.filter(recall => {
                                      const enhanced = enhancedRecalls.get(recall.assignmentId);
                                      // Include if no finalStatus, OR if has unsaved changes (statusUpdatedAt exists)
                                      // This keeps unsaved updates visible until they're saved
                                      return !enhanced?.finalStatus || !!enhanced?.statusUpdatedAt;
                                    });

                                    // Completed recalls: have finalStatus AND no unsaved changes
                                    const completedRecalls = route.recalls.filter(recall => {
                                      const enhanced = enhancedRecalls.get(recall.assignmentId);
                                      // Include if has finalStatus AND no unsaved changes
                                      return !!enhanced?.finalStatus && !enhanced?.statusUpdatedAt;
                                    });

                                    // Always show both pending and completed recalls
                                    const filteredPending = pendingRecalls;
                                    const filteredCompleted = completedRecalls;

                                    // Helper function to group and render recalls
                                    const renderRecallsGroup = (
                                      recalls: typeof route.recalls,
                                      sectionTitle: string,
                                      sectionIcon: React.ReactNode,
                                      statusBreakdown?: {
                                        label: string;
                                        count: number;
                                        color: string;
                                      }[]
                                    ) => {
                                      if (recalls.length === 0) return null;

                                      // Group recalls by dealer, then by VIN within each dealer
                                      const groupedByDealer = recalls.reduce(
                                        (acc, recall) => {
                                          // Customer stops are all 'Customer stop' — group by household
                                          const dealerKey = trackerLocationKey(recall);
                                          if (!acc[dealerKey]) acc[dealerKey] = {};

                                          const vinKey = recall.vin;
                                          if (!acc[dealerKey][vinKey]) acc[dealerKey][vinKey] = [];
                                          acc[dealerKey][vinKey].push(recall);
                                          return acc;
                                        },
                                        {} as Record<string, Record<string, typeof recalls>>
                                      );

                                      // Count unique vehicles for dealer badge
                                      const getVehicleCount = (
                                        vinGroups: Record<string, typeof recalls>
                                      ) => Object.keys(vinGroups).length;

                                      return (
                                        <div className='space-y-4'>
                                          {/* Section Header */}
                                          <div className='pb-2 border-b-2'>
                                            <div className='flex items-center gap-2'>
                                              {sectionIcon}
                                              <h4 className='font-semibold text-base'>
                                                {sectionTitle}
                                              </h4>
                                              <Badge variant='secondary' className='text-xs'>
                                                {recalls.length}{' '}
                                                {recalls.length === 1 ? 'recall' : 'recalls'}
                                              </Badge>
                                            </div>
                                            {statusBreakdown && statusBreakdown.length > 0 && (
                                              <div className='flex items-center gap-3 flex-wrap mt-1.5 pl-6'>
                                                {statusBreakdown.map(item => (
                                                  <span
                                                    key={item.label}
                                                    className='inline-flex items-center gap-1 text-xs'
                                                  >
                                                    <span
                                                      className='w-2 h-2 rounded-full inline-block flex-shrink-0'
                                                      style={{ backgroundColor: item.color }}
                                                    />
                                                    <span className='text-muted-foreground'>
                                                      {item.label}
                                                    </span>
                                                    <span className='font-semibold tabular-nums'>
                                                      {item.count}
                                                    </span>
                                                  </span>
                                                ))}
                                              </div>
                                            )}
                                          </div>

                                          {/* Grouped recalls by dealer, then by VIN */}
                                          {Object.entries(groupedByDealer).map(
                                            ([locationKey, vinGroups]) => {
                                              const firstVin = Object.keys(vinGroups)[0];
                                              const firstRecall = vinGroups[firstVin]?.[0];
                                              const isCustomer =
                                                firstRecall?.stopKind === 'customer';
                                              const dealerName = isCustomer
                                                ? CUSTOMER_STOP_LABEL
                                                : firstRecall?.dealerName || 'Unknown Dealer';
                                              const groupDispatcherNotes = Array.from(
                                                new Set(
                                                  Object.values(vinGroups)
                                                    .flat()
                                                    .map(r => r.dispatcherNote?.trim())
                                                    .filter((n): n is string => !!n)
                                                )
                                              );
                                              const dealerFullAddress = [
                                                firstRecall?.addressLine2
                                                  ? `${firstRecall.dealerAddress}, ${firstRecall.addressLine2}`
                                                  : firstRecall?.dealerAddress,
                                                firstRecall?.dealerCity,
                                                firstRecall?.dealerRegion,
                                                firstRecall?.dealerPostalCode
                                              ]
                                                .filter(Boolean)
                                                .join(', ');
                                              const vehicleCount = getVehicleCount(vinGroups);

                                              return (
                                                <div key={locationKey} className='space-y-2'>
                                                  {/* Dealer Header */}
                                                  <div className='flex flex-col gap-0.5 pb-1 border-b'>
                                                    <div className='flex items-center gap-2'>
                                                      {isCustomer ? (
                                                        <Home className='h-4 w-4 text-muted-foreground' />
                                                      ) : (
                                                        <MapPin className='h-4 w-4 text-muted-foreground' />
                                                      )}
                                                      <span className='font-semibold text-sm text-foreground'>
                                                        {dealerName}
                                                      </span>
                                                      <Badge
                                                        variant='secondary'
                                                        className='text-xs'
                                                      >
                                                        {vehicleCount}{' '}
                                                        {vehicleCount === 1
                                                          ? 'vehicle'
                                                          : 'vehicles'}
                                                      </Badge>
                                                    </div>
                                                    {dealerFullAddress && (
                                                      <span className='text-xs text-muted-foreground pl-6'>
                                                        {dealerFullAddress}
                                                      </span>
                                                    )}
                                                    <StopNotesAndContact
                                                      isCustomer={isCustomer}
                                                      contactId={firstRecall?.contactId}
                                                      dispatcherNotes={groupDispatcherNotes}
                                                    />
                                                  </div>

                                                  {/* Vehicles (grouped by VIN) at this dealer */}
                                                  <div className='space-y-3 pl-6'>
                                                    {Object.entries(vinGroups).map(
                                                      ([vin, vinRecalls]) => {
                                                        const vehicleFirstRecall = vinRecalls[0];
                                                        const vehicleDescription = [
                                                          vehicleFirstRecall?.vehicleYear,
                                                          vehicleFirstRecall?.vehicleMake,
                                                          vehicleFirstRecall?.vehicleModel
                                                        ]
                                                          .filter(Boolean)
                                                          .join(' ');
                                                        const hasMultipleRecalls =
                                                          vinRecalls.length > 1;

                                                        return (
                                                          <div
                                                            key={vin}
                                                            className='border rounded-lg p-4 space-y-3'
                                                          >
                                                            {/* Vehicle Header - shown once per VIN */}
                                                            <div
                                                              className='flex items-center justify-between
                                                                gap-2 pb-2 border-b'
                                                            >
                                                              <div className='flex items-center gap-2'>
                                                                <Car className='h-4 w-4 text-muted-foreground' />
                                                                <span className='font-semibold text-sm'>
                                                                  {vehicleDescription ||
                                                                    'Unknown Vehicle'}
                                                                </span>
                                                                {hasMultipleRecalls && (
                                                                  <Badge
                                                                    variant='outline'
                                                                    className='text-xs'
                                                                  >
                                                                    {vinRecalls.length} recalls
                                                                  </Badge>
                                                                )}
                                                              </div>
                                                              <CopyableVin
                                                                vin={vin}
                                                                className='text-xs'
                                                              />
                                                            </div>

                                                            {/* Recalls for this vehicle */}
                                                            <div className='space-y-3'>
                                                              {vinRecalls.map(recall => {
                                                                const enhanced =
                                                                  enhancedRecalls.get(
                                                                    recall.assignmentId
                                                                  );
                                                                const statusInfo =
                                                                  RECALL_STATUSES.find(
                                                                    s =>
                                                                      s.value ===
                                                                      enhanced?.finalStatus
                                                                  );
                                                                const otherReasonDisplay =
                                                                  enhanced?.finalStatus ===
                                                                    'other' &&
                                                                  enhanced?.otherReason ? (
                                                                    <span
                                                                      className={
                                                                        'max-w-[200px] block text-right ' +
                                                                        'text-xs text-muted-foreground'
                                                                      }
                                                                    >
                                                                      <span className='font-medium'>
                                                                        Reason:{' '}
                                                                      </span>
                                                                      <span className='italic'>
                                                                        {enhanced.otherReason}
                                                                      </span>
                                                                    </span>
                                                                  ) : null;

                                                                return (
                                                                  <div
                                                                    key={recall.assignmentId}
                                                                    className={cn(
                                                                      'rounded-lg p-3 space-y-3 ' +
                                                                        'transition-colors border-l-4',
                                                                      selectedRecalls.has(
                                                                        recall.assignmentId
                                                                      )
                                                                        ? 'bg-primary/10 dark:bg-primary/15 ' +
                                                                            'border-primary'
                                                                        : isManualStop(recall)
                                                                          ? 'bg-orange-50/50 ' +
                                                                            'dark:bg-orange-500/10 border-orange-500'
                                                                          : 'bg-muted/30 border-transparent'
                                                                    )}
                                                                  >
                                                                    {/* Recall Header - campaign info */}
                                                                    <div
                                                                      className='flex items-start justify-between
                                                                      gap-4'
                                                                    >
                                                                      <div
                                                                        className='flex items-start gap-3 flex-1
                                                                        min-w-0'
                                                                      >
                                                                        <input
                                                                          type='checkbox'
                                                                          checked={selectedRecalls.has(
                                                                            recall.assignmentId
                                                                          )}
                                                                          onChange={() =>
                                                                            toggleRecallSelection(
                                                                              recall.assignmentId
                                                                            )
                                                                          }
                                                                          disabled={
                                                                            !isRouteEditable
                                                                          }
                                                                          className={
                                                                            'mt-1 rounded flex-shrink-0 ' +
                                                                            'disabled:opacity-50 disabled:cursor-not-allowed'
                                                                          }
                                                                        />
                                                                        <div className='flex-1 min-w-0'>
                                                                          <div className='flex items-center gap-1.5 min-w-0'>
                                                                            {isManualStop(
                                                                              recall
                                                                            ) ? (
                                                                              <>
                                                                                <Wrench
                                                                                  className={
                                                                                    'h-3.5 w-3.5 flex-shrink-0 ' +
                                                                                    'text-orange-600 dark:text-orange-400'
                                                                                  }
                                                                                />
                                                                                <span
                                                                                  className={
                                                                                    'inline-flex items-center rounded ' +
                                                                                    'bg-orange-100 px-1.5 py-0.5 shrink-0 ' +
                                                                                    'text-xs font-medium text-orange-700 ' +
                                                                                    'dark:bg-orange-900/40 dark:text-orange-300'
                                                                                  }
                                                                                >
                                                                                  Manual Stop
                                                                                </span>
                                                                                {/* Stop Reason - full text */}
                                                                                {recall.manualStopReason && (
                                                                                  <span className='text-xs text-muted-foreground'>
                                                                                    -{' '}
                                                                                    {getManualStopReasonLabel(
                                                                                      recall.manualStopReason
                                                                                    )}
                                                                                  </span>
                                                                                )}
                                                                                {/* Campaign Code - if not a placeholder */}
                                                                                {recall.campaignNumber &&
                                                                                  recall.campaignNumber !==
                                                                                    'MANUAL' &&
                                                                                  !recall.campaignNumber.startsWith(
                                                                                    'MANUAL-'
                                                                                  ) && (
                                                                                    <span className='text-xs text-muted-foreground'>
                                                                                      -{' '}
                                                                                      {
                                                                                        recall.campaignNumber
                                                                                      }
                                                                                    </span>
                                                                                  )}
                                                                                {/* Notes - if present */}
                                                                                {recall.notes && (
                                                                                  <span
                                                                                    className='text-xs text-muted-foreground truncate min-w-0'
                                                                                    title={
                                                                                      recall.notes
                                                                                    }
                                                                                  >
                                                                                    - {recall.notes}
                                                                                  </span>
                                                                                )}
                                                                              </>
                                                                            ) : (
                                                                              <>
                                                                                <FileText
                                                                                  className={
                                                                                    'h-3 w-3 flex-shrink-0 ' +
                                                                                    'text-blue-600 dark:text-blue-400'
                                                                                  }
                                                                                />
                                                                                <span
                                                                                  className={
                                                                                    'inline-flex items-center rounded ' +
                                                                                    'bg-blue-100 px-1.5 py-0.5 shrink-0 ' +
                                                                                    'text-xs font-medium text-blue-700 ' +
                                                                                    'dark:bg-blue-900/40 dark:text-blue-300'
                                                                                  }
                                                                                >
                                                                                  {
                                                                                    recall.campaignNumber
                                                                                  }
                                                                                </span>
                                                                                {recall.recallTitle && (
                                                                                  <span
                                                                                    className='text-xs text-muted-foreground truncate min-w-0'
                                                                                    title={
                                                                                      recall.recallTitle
                                                                                    }
                                                                                  >
                                                                                    -{' '}
                                                                                    {
                                                                                      recall.recallTitle
                                                                                    }
                                                                                  </span>
                                                                                )}
                                                                              </>
                                                                            )}
                                                                          </div>
                                                                        </div>
                                                                      </div>
                                                                      <div className='flex items-center gap-2 flex-shrink-0'>
                                                                        {statusInfo && (
                                                                          <div className='flex flex-col items-end gap-1'>
                                                                            <Badge
                                                                              variant='outline'
                                                                              className='gap-1'
                                                                            >
                                                                              <statusInfo.icon
                                                                                className={cn(
                                                                                  'h-3 w-3',
                                                                                  statusInfo.color
                                                                                )}
                                                                              />
                                                                              {statusInfo.label}
                                                                            </Badge>
                                                                            {otherReasonDisplay}
                                                                          </div>
                                                                        )}
                                                                      </div>
                                                                    </div>

                                                                    {/* Status Update Controls */}
                                                                    <div className='grid grid-cols-1 md:grid-cols-2 gap-3 pt-2'>
                                                                      <div>
                                                                        <Label className='text-xs mb-1'>
                                                                          Status
                                                                        </Label>
                                                                        <Select
                                                                          value={
                                                                            enhanced?.finalStatus ||
                                                                            ''
                                                                          }
                                                                          onValueChange={value => {
                                                                            // Check if route date is in the future
                                                                            if (
                                                                              isRouteDateInFuture(
                                                                                route.routeDate
                                                                              )
                                                                            ) {
                                                                              toast({
                                                                                title:
                                                                                  'Cannot Update Status',
                                                                                description:
                                                                                  'You cannot update the status of recalls ' +
                                                                                  'scheduled for future dates.',
                                                                                variant:
                                                                                  'destructive'
                                                                              });
                                                                              return;
                                                                            }

                                                                            updateRecallStatus(
                                                                              recall.assignmentId,
                                                                              {
                                                                                finalStatus:
                                                                                  value as EnhancedRecallDetail['finalStatus']
                                                                              }
                                                                            );
                                                                          }}
                                                                          disabled={
                                                                            !isRouteEditable
                                                                          }
                                                                        >
                                                                          <SelectTrigger className='h-9'>
                                                                            <SelectValue placeholder='Select status' />
                                                                          </SelectTrigger>
                                                                          <SelectContent>
                                                                            {getStatusOptions(
                                                                              recall
                                                                            ).map(status => (
                                                                              <SelectItem
                                                                                key={status.value}
                                                                                value={status.value}
                                                                              >
                                                                                <div className='flex items-center gap-2'>
                                                                                  <status.icon
                                                                                    className={cn(
                                                                                      'h-4 w-4',
                                                                                      status.color
                                                                                    )}
                                                                                  />
                                                                                  {status.label}
                                                                                </div>
                                                                              </SelectItem>
                                                                            ))}
                                                                          </SelectContent>
                                                                        </Select>
                                                                      </div>

                                                                      <div>
                                                                        <Label className='text-xs mb-1'>
                                                                          Serviced By
                                                                        </Label>
                                                                        <Select
                                                                          value={
                                                                            enhanced?.servicedByTechnicianId ||
                                                                            route.technicianId
                                                                          }
                                                                          onValueChange={value => {
                                                                            const tech =
                                                                              technicians.find(
                                                                                t => t.id === value
                                                                              );
                                                                            updateRecallStatus(
                                                                              recall.assignmentId,
                                                                              {
                                                                                servicedByTechnicianId:
                                                                                  value,
                                                                                servicedByTechnicianName:
                                                                                  tech?.name || ''
                                                                              }
                                                                            );
                                                                          }}
                                                                          disabled={
                                                                            !isRouteEditable
                                                                          }
                                                                        >
                                                                          <SelectTrigger className='h-9'>
                                                                            <SelectValue placeholder='Select technician' />
                                                                          </SelectTrigger>
                                                                          <SelectContent>
                                                                            {technicians.map(
                                                                              tech => (
                                                                                <SelectItem
                                                                                  key={tech.id}
                                                                                  value={tech.id}
                                                                                >
                                                                                  {tech.name}
                                                                                </SelectItem>
                                                                              )
                                                                            )}
                                                                          </SelectContent>
                                                                        </Select>
                                                                      </div>
                                                                    </div>

                                                                    {/* Reason dropdown - Only for recalls with "Other" status (not manual stops) */}
                                                                    {!isManualStop(recall) &&
                                                                      enhanced?.finalStatus ===
                                                                        'other' && (
                                                                        <div className='space-y-2'>
                                                                          <Label className='text-xs'>
                                                                            Reason
                                                                            <span className='text-destructive'>
                                                                              {' '}
                                                                              *
                                                                            </span>
                                                                          </Label>
                                                                          <Select
                                                                            value={
                                                                              enhanced.otherReason ||
                                                                              ''
                                                                            }
                                                                            onValueChange={value =>
                                                                              updateRecallStatus(
                                                                                recall.assignmentId,
                                                                                {
                                                                                  otherReason: value
                                                                                }
                                                                              )
                                                                            }
                                                                            disabled={
                                                                              !isRouteEditable
                                                                            }
                                                                          >
                                                                            <SelectTrigger className='h-9'>
                                                                              <SelectValue placeholder='Select reason' />
                                                                            </SelectTrigger>
                                                                            <SelectContent>
                                                                              {COMMON_REASONS.map(
                                                                                reason => (
                                                                                  <SelectItem
                                                                                    key={reason}
                                                                                    value={reason}
                                                                                  >
                                                                                    <span className='flex items-center gap-2'>
                                                                                      {reason}
                                                                                      {isRecyclingReason(
                                                                                        reason
                                                                                      ) && (
                                                                                        <Badge
                                                                                          variant='secondary'
                                                                                          className='text-[10px] font-normal'
                                                                                        >
                                                                                          returns to
                                                                                          pool
                                                                                        </Badge>
                                                                                      )}
                                                                                    </span>
                                                                                  </SelectItem>
                                                                                )
                                                                              )}
                                                                            </SelectContent>
                                                                          </Select>
                                                                          <p className='mt-1 text-xs text-muted-foreground'>
                                                                            Reasons marked
                                                                            &ldquo;returns to
                                                                            pool&rdquo; send this
                                                                            recall back for
                                                                            reassignment. The others
                                                                            close it permanently.
                                                                          </p>
                                                                        </div>
                                                                      )}

                                                                    {/* Notes textarea - Show for ALL statuses */}
                                                                    {enhanced?.finalStatus && (
                                                                      <div className='space-y-2'>
                                                                        <Label className='text-xs'>
                                                                          Notes
                                                                          {!isManualStop(recall) &&
                                                                            enhanced.finalStatus ===
                                                                              'other' && (
                                                                              <span className='text-destructive'>
                                                                                {' '}
                                                                                *
                                                                              </span>
                                                                            )}
                                                                          {isManualStop(recall) && (
                                                                            <span className='text-muted-foreground ml-1'>
                                                                              (optional)
                                                                            </span>
                                                                          )}
                                                                        </Label>
                                                                        <Textarea
                                                                          placeholder={
                                                                            isManualStop(recall)
                                                                              ? 'Add optional notes about this stop...'
                                                                              : enhanced.finalStatus ===
                                                                                  'other'
                                                                                ? 'Enter notes (required)...'
                                                                                : 'Add optional notes...'
                                                                          }
                                                                          value={
                                                                            enhanced.statusNotes ||
                                                                            ''
                                                                          }
                                                                          onChange={e =>
                                                                            updateRecallStatus(
                                                                              recall.assignmentId,
                                                                              {
                                                                                statusNotes:
                                                                                  e.target.value
                                                                              }
                                                                            )
                                                                          }
                                                                          disabled={
                                                                            !isRouteEditable
                                                                          }
                                                                          className='h-16 resize-none'
                                                                        />
                                                                      </div>
                                                                    )}
                                                                  </div>
                                                                );
                                                              })}
                                                            </div>
                                                          </div>
                                                        );
                                                      }
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            }
                                          )}
                                        </div>
                                      );
                                    };

                                    // (Removed completedBreakdown — it fed the deleted "Updated"
                                    // subsection header. Resolved-status counts now live in the
                                    // BOTTOM Status Updates section's per-tech cards.)

                                    // Only pending recalls render inline. Resolved (completed/released)
                                    // items now live in the Status Updates section below — keeps the
                                    // top "Needs Attention" surface focused on action work only.
                                    return renderRecallsGroup(
                                      filteredPending,
                                      'Pending',
                                      <AlertCircle className='h-4 w-4 text-orange-500' />
                                    );
                                  })()}
                                </div>
                              </CardContent>
                            </CollapsibleContent>
                          </Collapsible>
                        )}
                      </Card>
                    );
                  })}

                {/* BOTTOM section — per-technician day report with the Daily Route Summary
                    entry point. Replaces the prior status→dealer→VIN "Recently Updated"
                    block; the data shape and per-recall click-to-drawer wiring are preserved. */}
                <StatusUpdatesSection
                  routes={routes}
                  serviceEventsMap={serviceEventsMap}
                  selectedDate={selectedDate}
                  onOpenWorkSummary={recall => {
                    setWorkSummaryRecall(recall);
                    setWorkSummaryOpen(true);
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Floating Save Reminder - positioned above fixed footer */}
        {unsavedChanges && (
          <div className='fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50'>
            <Alert className='w-auto shadow-lg border-orange-200 bg-orange-50 dark:bg-orange-950/90'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription className='pr-8'>
                You have unsaved changes. Don&apos;t forget to save!
              </AlertDescription>
            </Alert>
          </div>
        )}

        <WorkSummaryDrawer
          recall={workSummaryRecall}
          open={workSummaryOpen}
          onOpenChange={open => {
            setWorkSummaryOpen(open);
            if (!open) setWorkSummaryRecall(null);
          }}
        />
      </div>
    </SecurityBoundary>
  );
};

export default RouteTrackerPageV3;
